/**
 * Helpers for dynamic-pricing data (the "length" / "size" option groups).
 *
 * The services payload can arrive in more than one shape, so these helpers
 * collapse every known variant into a single predictable form: UI code never
 * has to branch on it, and never crashes when a nested field is not the
 * array/object it is expected to be.
 */

/**
 * Parse `dynamic_prices` into an object, whether it arrives as an already
 * parsed object (current API) or a JSON string (legacy API). Always returns an
 * object; never throws.
 */
export const parseDynamicPrices = (raw: unknown): any => {
  if (!raw) return {};

  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }

  return typeof raw === "object" ? raw : {};
};

/**
 * Reduce a single dynamic-price value to its id string, accepting either a
 * bare id string (current API) or a `{ id | _id }` object (legacy API).
 */
export const getValueId = (value: any): string => {
  if (typeof value === "string") return value;

  if (value && typeof value === "object") return value._id ?? value.id ?? "";

  return "";
};

/**
 * Normalise a group's `values` array into a clean list of id strings.
 * Returns `[]` for any non-array / missing input.
 */
export const getValueIds = (values: any): string[] =>
  Array.isArray(values) ? values.map(getValueId).filter(Boolean) : [];

const getGroupValues = (extras: any[] | undefined | null, group: string) => {
  if (!Array.isArray(extras)) return [];

  const extra = extras.find((e: any) => e && e._id === group);

  return Array.isArray(extra?.values) ? extra.values : [];
};

const findExtraValue = (
  extras: any[] | undefined | null,
  group: string,
  optionId: string | undefined
) =>
  getGroupValues(extras, group).find(
    (v: any) => v && (v.id === optionId || v._id === optionId)
  );

/**
 * Resolve the human-readable label for an option id from the extras catalog.
 * Fully null/array-safe and tolerant of `id` vs `_id` on the value objects.
 * Returns '' when nothing matches.
 */
export const getExtraLabel = (
  extras: any[] | undefined | null,
  group: string,
  optionId: string | undefined
): string => findExtraValue(extras, group, optionId)?.name ?? "";

/**
 * A group's option ids in display order, top to bottom.
 *
 * The extras catalog is already returned in display order, so it is the sole
 * source of truth for ordering: we keep the catalog's sequence and narrow it to
 * the options this service actually offers. A service's own `values` array is
 * *not* reliably ordered — some are stored reversed or shuffled — so it only
 * tells us which options exist, never in what order they belong.
 *
 * Ids the catalog does not list for this group are dropped: they have no label
 * and no defensible position, and letting one land at the base end would make it
 * the free option.
 *
 * Falls back to the service's own order only while the catalog is still loading.
 */
export const getOrderedOptionIds = (
  extras: any[] | undefined | null,
  group: string,
  values: any
): string[] => {
  const ids = getValueIds(values);
  if (ids.length === 0) return ids;

  const catalogIds = getGroupValues(extras, group)
    .map((v: any) => (v ? v.id ?? v._id ?? "" : ""))
    .filter(Boolean);

  if (catalogIds.length === 0) return ids;

  return catalogIds.filter((id: string) => ids.indexOf(id) !== -1);
};

/**
 * The base option of a group, the one that costs nothing on top of the service
 * price. Always the first option in catalog order — the catalog's `rank` *is*
 * the price hierarchy, so rank 1 is the free option for every group ("Shoulder
 * length" for length, "Jumbo" for size). '' when the group is empty.
 *
 * The server ranks both groups the same way (`indexOf` on the same catalog
 * order, see `Helper.calculateServicePriceAndDuration`), and it rejects any
 * booking whose `gross_amount` does not match what it recomputes, so this must
 * not diverge per group.
 */
export const getBaseOptionId = (orderedIds: string[]): string =>
  orderedIds.length === 0 ? "" : orderedIds[0];

/**
 * How many steps the selected option sits below its group's base option, i.e.
 * the multiplier for `additional_price` / `additional_duration`. The base option
 * is 0, the next one down is 1, and so on. Unknown selections count as base.
 */
export const getPriceStep = (
  orderedIds: string[],
  selectedId: string | undefined
): number => {
  const index = selectedId ? orderedIds.indexOf(selectedId) : -1;

  return index < 0 ? 0 : index;
};
